import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom-v5-compat'

import AddIcon from '@mui/icons-material/Add'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import MuiTableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import dayjs from 'dayjs'
import Cookie from 'js-cookie'

import { useAppSelector } from '~/store'
import { formatDate } from '~/utils'
import { getPath } from '../../oakra/components/base-app'
import { patch } from '../../tools/request'
import { getSorting, stableSort } from '../../tools/table-tools'
import { copyObject } from '../../tools/utils'

const TableCell = styled(MuiTableCell)({
    padding: '0 12px',
})

const DiscountLink = styled(Link)({
    textDecoration: 'none',
    color: 'inherit',
})

function shouldDisableDelete(discountStartTime) {
    if (!discountStartTime) {
        return false
    }

    const MAX_MINUTES = 60
    const MIN_MINUTES = 0
    const startTime = dayjs(discountStartTime)
    const diffInMinutes = dayjs().diff(startTime, 'minute')

    return diffInMinutes > MIN_MINUTES && diffInMinutes < MAX_MINUTES
}

const columns = [
    { id: 'name', align: 'left', label: gettext('Name'), sort: true, width: 250 },
    { id: 'store__name', align: 'left', label: gettext('Store'), sort: true },
    { id: 'start_date', align: 'left', label: gettext('Start date'), sort: true },
    { id: 'end_date', align: 'left', label: gettext('End date'), sort: true },
    { id: 'status', align: 'left', label: gettext('Status'), sort: true },
    { id: 'num_products', align: 'left', label: gettext('# of products'), sort: true },
    { id: 'active', align: 'center', label: gettext('Active'), sort: false, width: 100 },
    { id: 'more_button', align: 'left', label: '', sort: false, width: 80 },
]

function DiscountActionsButton({ onCopyDiscount }) {
    const [anchorEl, setAnchorEl] = useState(null)

    return (
        <div>
            <IconButton
                aria-label="more"
                aria-controls="three-dots-menu"
                aria-haspopup="true"
                onClick={(event) => setAnchorEl(event.currentTarget)}
            >
                <MoreVertIcon />
            </IconButton>
            <Menu
                id="three-dots-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem
                    onClick={() => {
                        onCopyDiscount()
                        setAnchorEl(null)
                    }}
                >
                    {gettext('Copy discount')}
                </MenuItem>
            </Menu>
        </div>
    )
}

function DiscountsTableHead(props) {
    const { orderBy, numSelected, rowCount, onSelectAllClick, onRequestSort } = props

    const createSortHandler = (property) => {
        return (event) => {
            onRequestSort(event, property)
        }
    }

    return (
        <TableHead sx={{ borderTop: (theme) => '1px solid ' + theme.palette.grey[200] }}>
            <TableRow>
                <TableCell sx={{ width: 70 }}>
                    <Checkbox
                        color="secondary"
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount !== 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                    />
                </TableCell>
                {columns.map((column) => {
                    return (
                        <TableCell
                            key={column.id}
                            align={column.align}
                            sortDirection={
                                orderBy.replace('-', '') === column.id &&
                                (orderBy.startsWith('-') ? 'desc' : 'asc')
                            }
                            width={column.width}
                        >
                            {column.sort ? (
                                <TableSortLabel
                                    active={orderBy.replace('-', '') === column.id}
                                    direction={orderBy.startsWith('-') ? 'desc' : 'asc'}
                                    onClick={createSortHandler(column.id)}
                                >
                                    {column.label}
                                </TableSortLabel>
                            ) : (
                                column.label
                            )}
                        </TableCell>
                    )
                }, this)}
            </TableRow>
        </TableHead>
    )
}

const DISCOUNT_STATUS = ['expired', 'current', 'upcoming']

function DiscountsTableToolbar({
    numSelected,
    selectedShopID,
    selectedStatus,
    page,
    count,
    handleChangePage,
    handleDelete,
    handleNewDiscountClick,
    handleShopChange,
    handleStatusSelectorChange,
}) {
    const stores = useAppSelector((state) => state.stores)
    const hasSelected = numSelected > 0

    return (
        <Toolbar
            sx={{
                pr: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: hasSelected ? 'secondary.dark' : undefined,
                backgroundColor: hasSelected ? 'secondary.light' : undefined,
            }}
        >
            <div>
                {hasSelected ? (
                    <Typography color="inherit" variant="subtitle1">
                        {interpolate(pgettext('Number selected', '%s selected'), [numSelected])}
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControl variant="outlined" size="small" sx={{ width: 200 }}>
                            <InputLabel shrink id="store-select-label">
                                {gettext('Store')}
                            </InputLabel>
                            <Select
                                displayEmpty
                                labelId="store-select-label"
                                label={gettext('Store')}
                                value={selectedShopID}
                                onChange={handleShopChange}
                            >
                                <MenuItem value="all" key="all">
                                    {pgettext('all stores', 'All')}
                                </MenuItem>
                                {stores.map((store) => (
                                    <MenuItem value={store.id} key={store.id}>
                                        {store.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl
                            size="small"
                            variant="outlined"
                            sx={{ width: 200, textTransform: 'capitalize' }}
                        >
                            <InputLabel shrink id="status-select-label">
                                {gettext('Status')}
                            </InputLabel>
                            <Select
                                displayEmpty
                                labelId="status-select-label"
                                label={gettext('Status')}
                                value={selectedStatus}
                                onChange={handleStatusSelectorChange}
                            >
                                <MenuItem value="all" key="all">
                                    {gettext('All')}
                                </MenuItem>
                                {DISCOUNT_STATUS.map((value) => (
                                    <MenuItem
                                        key={value}
                                        value={value}
                                        sx={{ textTransform: 'capitalize' }}
                                    >
                                        {gettext(value)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}
            </div>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {hasSelected ? (
                    <Button color="secondary" onClick={handleDelete}>
                        {ngettext('Delete discount', 'Delete discounts', numSelected)}
                    </Button>
                ) : (
                    <>
                        <div>
                            {stores.length === 1 ? (
                                <Button
                                    startIcon={<AddIcon />}
                                    component={Link}
                                    to={getPath('createDiscount').replace('store_id', stores[0].id)}
                                    variant="outlined"
                                    color="primary"
                                >
                                    {gettext('New discount')}
                                </Button>
                            ) : (
                                <Button
                                    startIcon={<AddIcon />}
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleNewDiscountClick}
                                >
                                    {gettext('New discount')}
                                </Button>
                            )}
                        </div>
                        <TablePagination
                            component="div"
                            count={count}
                            rowsPerPage={25}
                            page={page}
                            rowsPerPageOptions={[25]}
                            slotProps={{
                                actions: {
                                    previousButton: {
                                        'aria-label': gettext('Previous'),
                                    },
                                    nextButton: {
                                        'aria-label': gettext('Next'),
                                    },
                                },
                            }}
                            onPageChange={handleChangePage}
                        />
                    </>
                )}
            </Box>
        </Toolbar>
    )
}

export default function Discounts() {
    const navigate = useNavigate()
    const language = useAppSelector((state) => state.initial.language)
    const stores = useAppSelector((state) => state.stores)
    const accountOwnerID = useAppSelector((state) => state.initial.accountOwner.id)
    const { discountsForStoreAPIURL, discountsForUserAPIURL, discountAPIURL } = useAppSelector(
        (state) => state.initial.endpoints
    )

    const [orderBy, setOrderBy] = useState('-start_date')
    const [selected, setSelected] = useState([])
    const [rowsPerPage] = useState(25)
    const [selectedShopID, setSelectedShopID] = useState('all')
    const [discounts, setDiscounts] = useState([])
    const [count, setCount] = useState(0)
    const [page, setPage] = useState(0)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [newProductSelectShopOpen, setNewProductSelectShopOpen] = useState(false)
    const [loading, setLoading] = useState(true)
    const [selectedStatus, setSelectedStatus] = useState('all')

    useEffect(() => {
        fetchDiscountsData(getDataURL(), false)
    }, [])

    const addStoresInfoToDiscountsData = (data) => {
        const storesById = stores.reduce((allById, store) => {
            allById[store.id] = store
            return allById
        }, {})
        return data.map((discount) => ({ ...discount, storeName: storesById[discount.store].name }))
    }

    const fetchDiscountsData = (url, replace, page = null, _searchTerm = null) => {
        if (!url.includes('order_by')) {
            url += '?order_by=' + orderBy
        }

        if (selectedStatus != 'all') {
            url += '&status=' + selectedStatus
        }

        return fetch(url.replace('store_pk', selectedShopID))
            .then((response) => response.json())
            .then((data) => {
                let newDiscounts

                data = addStoresInfoToDiscountsData(data)

                if (replace) {
                    newDiscounts = data
                } else {
                    newDiscounts = copyObject(discounts).concat(data)
                }

                setDiscounts(newDiscounts)
                setCount(newDiscounts.length)
                setLoading(false)

                if (page === 0) {
                    setPage(0)
                } else if (page > 0) {
                    setPage(page)
                }
            })
    }

    const getDataURL = () => {
        if (selectedShopID === 'all') {
            return discountsForUserAPIURL.replace('user_pk', accountOwnerID)
        } else {
            return discountsForStoreAPIURL.replace('store_pk', selectedShopID)
        }
    }

    const handleShopChange = (e) => {
        setSelectedShopID(e.target.value)
        setLoading(true)
        fetchDiscountsData(getDataURL(), true, 0)
    }

    const handleStatusSelectorChange = (e) => {
        setSelectedStatus(e.target.value)
        setLoading(true)
        fetchDiscountsData(getDataURL(), true, 0)
    }

    const handleSort = (_event, property) => {
        let newOrderBy

        if (orderBy.startsWith('-') && orderBy.replace('-', '') == property) {
            newOrderBy = property
        } else {
            newOrderBy = '-' + property
        }

        setOrderBy(newOrderBy)
        fetchDiscountsData(getDataURL(), true, 0)
    }

    const handleSelectAllClick = (_event, checked) => {
        if (checked) {
            setSelected(discounts.map((d) => d.id))
            return
        }
        setSelected([])
    }

    const handleChecked = (_event, checked, productID) => {
        let newSelected = [...selected]

        if (checked) {
            if (!selected.includes(productID)) {
                newSelected.push(productID)
            }
        } else {
            newSelected = newSelected.filter((id) => id != productID)
        }

        setSelected(newSelected)
    }

    const handleChangePage = (_event, newPage) => {
        setPage(newPage)
    }

    const handleDeleteConfirmClose = (confirm) => {
        return () => {
            if (confirm) {
                const newDiscounts = copyObject(discounts).filter((d) => !selected.includes(d.id))
                const selectedCopy = copyObject(selected)

                let newPage = page
                if (page > 0 && (page + 1) * rowsPerPage > newDiscounts.length) {
                    newPage = page - 1
                }

                setSelected([])
                setCount(newDiscounts.length)
                setDiscounts(newDiscounts)
                setDeleteConfirmOpen(false)
                setPage(newPage)

                selectedCopy.forEach((discountID) => {
                    fetch(discountAPIURL.replace('discount_id', discountID), {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json; charset=utf-8',
                            'X-CSRFToken': Cookie.get('csrftoken'),
                        },
                    }).then((response) => response.json())
                })
            } else {
                setDeleteConfirmOpen(false)
            }
        }
    }

    const handleDelete = () => {
        setDeleteConfirmOpen(true)
    }

    const handleNewDiscountClick = () => {
        setNewProductSelectShopOpen(true)
    }

    const handleNewDiscountSelectShopClose = () => {
        setNewProductSelectShopOpen(false)
    }

    const handleActiveSwitch = (discountId, checked) => {
        const payload = { is_active: checked }
        const newDiscounts = discounts.map((discount) => {
            if (discount.id === discountId) {
                return { ...discount, is_active: checked }
            }
            return { ...discount }
        })

        setDiscounts(newDiscounts)
        patch(discountAPIURL.replace('discount_id', discountId), payload)
            .then((response) => {
                console.log('update discount', response)
            })
            .catch((error) => {
                console.log('update discount error', error)
            })
    }

    const isSelected = (discountID) => {
        return selected.includes(discountID)
    }

    const displayedDiscounts = stableSort(discounts, getSorting(orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    )

    useEffect(() => {
        dayjs.locale(language)
    }, [language])

    return (
        <>
            <Paper sx={{ width: '100%', position: 'relative' }}>
                <DiscountsTableToolbar
                    numSelected={selected.length}
                    selectedShopID={selectedShopID}
                    handleShopChange={handleShopChange}
                    handleStatusSelectorChange={handleStatusSelectorChange}
                    selectedStatus={selectedStatus}
                    handleChangePage={handleChangePage}
                    handleDelete={handleDelete}
                    handleNewDiscountClick={handleNewDiscountClick}
                    count={count}
                    page={page}
                />
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table
                        sx={{ width: '100%', tableLayout: 'fixed' }}
                        aria-labelledby="tableTitle"
                    >
                        <DiscountsTableHead
                            orderBy={orderBy}
                            numSelected={selected.length}
                            rowCount={discounts.length}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleSort}
                        />
                        <TableBody>
                            {!loading && discounts.length == 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        sx={{ height: 200, py: 10 }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                placeItems: 'center',
                                            }}
                                        >
                                            <Stack alignItems="center" gap={1}>
                                                <LocalOfferIcon
                                                    color="disabled"
                                                    sx={{ fontSize: 100 }}
                                                />
                                                <Typography
                                                    color="text.disabled"
                                                    variant="subtitle1"
                                                >
                                                    {gettext('No discounts to display')}
                                                </Typography>
                                                <Button
                                                    startIcon={<AddIcon />}
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={handleNewDiscountClick}
                                                >
                                                    {gettext('New discount')}
                                                </Button>
                                            </Stack>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                            {displayedDiscounts.map((discount) => {
                                const isItemSelected = isSelected(discount.id)

                                return (
                                    <TableRow
                                        hover
                                        role="checkbox"
                                        key={discount.id}
                                        tabIndex={-1}
                                        selected={isItemSelected}
                                        aria-checked={isItemSelected}
                                        sx={{
                                            '&.Mui-selected': {
                                                backgroundColor: 'secondary.light',
                                                '&:hover': {
                                                    backgroundColor: 'secondary.light',
                                                },
                                            },
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                color="secondary"
                                                checked={isItemSelected}
                                                disabled={shouldDisableDelete(discount.start_date)}
                                                onChange={(event, checked) =>
                                                    handleChecked(event, checked, discount.id)
                                                }
                                            />
                                        </TableCell>

                                        <TableCell scope="row" padding="none">
                                            <DiscountLink
                                                to={getPath('discount').replace(
                                                    'discount_id',
                                                    discount.id
                                                )}
                                            >
                                                <Typography noWrap variant="body2">
                                                    {discount.name}
                                                </Typography>
                                            </DiscountLink>
                                        </TableCell>
                                        {selectedShopID == 'all' && (
                                            <TableCell scope="row" padding="none">
                                                <DiscountLink
                                                    to={getPath('discount').replace(
                                                        'discount_id',
                                                        discount.id
                                                    )}
                                                >
                                                    <Typography noWrap variant="body2">
                                                        {discount.storeName}
                                                    </Typography>
                                                </DiscountLink>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <DiscountLink
                                                to={getPath('discount').replace(
                                                    'discount_id',
                                                    discount.id
                                                )}
                                            >
                                                <Typography noWrap variant="body2">
                                                    {discount.start_date
                                                        ? formatDate(discount.start_date)
                                                        : '-'}
                                                </Typography>
                                            </DiscountLink>
                                        </TableCell>
                                        <TableCell>
                                            <DiscountLink
                                                to={getPath('discount').replace(
                                                    'discount_id',
                                                    discount.id
                                                )}
                                            >
                                                <Typography noWrap={true} variant="body2">
                                                    {discount.end_date
                                                        ? formatDate(discount.end_date)
                                                        : '-'}
                                                </Typography>
                                            </DiscountLink>
                                        </TableCell>
                                        {selectedStatus == 'all' && (
                                            <TableCell scope="row" padding="none">
                                                <DiscountLink
                                                    to={getPath('discount').replace(
                                                        'discount_id',
                                                        discount.id
                                                    )}
                                                >
                                                    <Typography noWrap={true} variant="body2">
                                                        {gettext(discount.status)
                                                            .charAt(0)
                                                            .toUpperCase() +
                                                            gettext(discount.status).slice(1)}
                                                    </Typography>
                                                </DiscountLink>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <DiscountLink
                                                to={getPath('discount').replace(
                                                    'discount_id',
                                                    discount.id
                                                )}
                                            >
                                                <Typography noWrap={true} variant="body2">
                                                    {discount.num_products}
                                                </Typography>
                                            </DiscountLink>
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                color="secondary"
                                                checked={discount.is_active}
                                                disabled={discount.status === 'expired'}
                                                onChange={(_e, checked) =>
                                                    handleActiveSwitch(discount.id, checked)
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <DiscountActionsButton
                                                onCopyDiscount={() => {
                                                    navigate(
                                                        `${getPath('createDiscount').replace(
                                                            'store_id',
                                                            discount.store
                                                        )}/?copy_discount_id=${discount.id}`
                                                    )
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    rowsPerPageOptions={[25]}
                    slotProps={{
                        actions: {
                            previousButton: {
                                'aria-label': gettext('Previous'),
                            },
                            nextButton: {
                                'aria-label': gettext('Next'),
                            },
                        },
                    }}
                    onPageChange={handleChangePage}
                />
            </Paper>

            <Dialog
                open={deleteConfirmOpen}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                onClose={handleDeleteConfirmClose(false)}
            >
                <DialogTitle id="alert-dialog-title">
                    {ngettext('Delete this discount?', 'Delete these discounts?', selected.length)}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {ngettext(
                            'It will be removed from all linked platforms.',
                            'They will be removed from all linked platforms.',
                            selected.length
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteConfirmClose(false)} color="primary">
                        {gettext('Cancel')}
                    </Button>
                    <Button autoFocus color="primary" onClick={handleDeleteConfirmClose(true)}>
                        {gettext('Confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                fullWidth
                open={newProductSelectShopOpen}
                maxWidth="xs"
                aria-labelledby="select-store"
                onClose={handleNewDiscountSelectShopClose}
            >
                <DialogTitle id="select-store">{gettext('Select store')}</DialogTitle>
                <div>
                    <List>
                        {stores.map((store) => (
                            <DiscountLink
                                key={store.id}
                                to={getPath('createDiscount').replace('store_id', store.id)}
                                onClick={handleNewDiscountSelectShopClose}
                            >
                                <ListItemButton>
                                    <ListItemText primary={store.name} />
                                </ListItemButton>
                            </DiscountLink>
                        ))}
                    </List>
                </div>
            </Dialog>
        </>
    )
}
